import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.css';

/**
 * A minimalist chat widget that talks to the Netlify function
 * `/.netlify/functions/iris-langchain` which in turn uses LangChain + OpenAI.
 * Designed from scratch to be < 200 lines and easy to extend.
 */
export default function LangchainChatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hi! I'm IRIS, your Impact AI assistant. Please choose an expert agent to begin." }
  ]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const [agent, setAgent] = useState(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const chatboxRef = useRef(null);
  // State to track if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Monitor for keyboard opening on mobile
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        // Detect virtual keyboard on mobile by checking window height change
        const viewportHeight = window.innerHeight;
        const keyboardThreshold = window.innerWidth < 480 && viewportHeight < window.outerHeight * 0.75;
        
        setKeyboardOpen(keyboardThreshold);
        
        // Apply class to chatbox when keyboard is detected
        if (chatboxRef.current) {
          if (keyboardThreshold) {
            chatboxRef.current.classList.add('keyboard-open');
          } else {
            chatboxRef.current.classList.remove('keyboard-open');
          }
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Debug state changes
  useEffect(() => {
    console.log('Chatbot state updated:', { open, agent, keyboardOpen });
    
    // Make sure the chatbot container is visible when opened
    if (open) {
      const container = document.querySelector('.chatbot-container');
      if (container) {
        container.style.display = 'block';
        container.style.visibility = 'visible';
      }
    }
  }, [open, agent, keyboardOpen]);

  // Auto-scroll to newest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Dynamic suggestions based on selected agent
  const getSuggestions = () => {
    const schedule = { label: 'Schedule a call', url: 'https://www.calendly.com/clarityimpactfinance' };
    if (!agent) return [];
    switch (agent) {
      case 'nmtc':
        return [
          { label: 'What NMTC services do you provide?', text: 'What NMTC services do you provide?' },
          schedule
        ];
      case 'cdfi':
        return [
          { label: 'What CDFI services do you provide?', text: 'What CDFI services do you provide?' },
          schedule
        ];
      case 'charter':
        return [
          { label: 'What charter school lending services do you provide?', text: 'What charter school lending services do you provide?' },
          schedule
        ];
      default:
        return [
          { label: 'What services do you provide?', text: 'What services do you provide?' },
          schedule
        ];
    }
  };

  const sendTextMessage = async (userText) => {
    setMessages((prev) => [...prev, { from: 'user', text: userText }]);
    setLoading(true);

    try {
      console.log('Sending request to API:', { message: userText, agent });
      const res = await fetch('/.netlify/functions/iris-fallback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userText, agent })
      });
      console.log('Response status:', res.status);

      if (!res.ok) {
        const errorText = await res.text();
        console.error('API error response:', errorText);
        throw new Error(`API error: ${res.status} - ${errorText}`);
      }
      
      try {
        const data = await res.json();
        console.log('Received chatbot response:', data);
        const answer = data.answer || 'Sorry, something went wrong.';
        setMessages((prev) => [...prev, { from: 'bot', text: answer }]);
      } catch (parseError) {
        console.error('Error parsing JSON response:', parseError);
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error in chatbot:', err);
      
      // More detailed error message to help debugging
      let errorMsg = 'Error: unable to fetch response.';
      
      if (err.message.includes('API error')) {
        errorMsg = `Error connecting to AI service. Please try again later. (${err.message})`;
      } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        errorMsg = 'Network error: Please check your internet connection and try again.';
      } else if (err.message.includes('JSON')) {
        errorMsg = 'Error: Received invalid response from server.';
      }
      
      setMessages((prev) => [...prev, { from: 'bot', text: errorMsg }]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setInput('');
    await sendTextMessage(userText);
  };

  const handleSuggestionClick = (item) => {
    if (item.url) {
      window.open(item.url, '_blank');
      return;
    }
    // If the suggestion is a question, send it straight away
    sendTextMessage(item.text);
  };

  // Set desktop style only if not mobile
  const desktopStyle = isMobile ? {} : {
    width: '420px',
    height: '600px',
    minHeight: '500px'
  };

  return (
    <div className="chatbot-container">
      {!open && (
        <button 
          className="chatbot-button" 
          onClick={() => {
            console.log('Opening chatbot');
            setOpen(true);
          }} 
          aria-label="Open chatbot"
        >
          <span className="chatbot-icon">💬</span> <span>Ask IRIS</span>
        </button>
      )}

      {open && !agent && (
        <div className="chatbot-box agent-selection-view" style={desktopStyle}>
          <header className="chatbot-header">
            <h3>Select an AI Expert</h3>
            <button className="close-button" onClick={() => setOpen(false)} aria-label="Close chatbot">×</button>
          </header>
          <div className="option-buttons agent-options">
            {[
              { label: 'NMTC Agent', val: 'nmtc' },
              { label: 'CDFI Agent', val: 'cdfi' },
              { label: 'Charter School Agent', val: 'charter' },
              { label: 'General Questions', val: 'general' }
            ].map(a => (
              <button
                key={a.val}
                className="option-button"
                onClick={() => {
                  setAgent(a.val);
                  const introMap = {
                    nmtc: "You've selected the NMTC Agent. Clarity provides New Markets Tax Credit services including program management, NMTC manuals, asset management, compliance, pipeline development, and closing assistance. How can I help you?",
                    cdfi: "You've selected the CDFI Agent. Clarity provides CDFIs with lending strategy and capitalization support including business model analysis, fund development and launch, process mapping, and underwriting. How can I assist you?",
                    charter: "You've selected the Charter School Agent. Clarity supports CDFIs with charter school lending pipeline development, as well as underwriting and closing. What would you like to know?",
                    general: "You've selected General Questions. Feel free to ask anything about Clarity Impact Finance or our services."
                  };
                  setMessages((prev) => [...prev, { from: 'bot', text: introMap[a.val] }]);
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {open && agent && (
        <div className={`chatbot-box ${keyboardOpen ? 'keyboard-open' : ''}`} 
          style={desktopStyle}
          ref={chatboxRef}>
          <header className="chatbot-header">
            <h3>IRIS – AI Assistant</h3>
            <button className="clear-chat-button" title="Change agent" onClick={() => {
              setAgent(null);
              setMessages([{ from: 'bot', text: "Please choose an expert agent to begin." }]);
            }}>↩</button>
            <button className="close-button" onClick={() => setOpen(false)} aria-label="Close chatbot">×</button>
          </header>

          <main className="messages-container">
            {messages.map((m, i) => (
              <div key={i} className={`message ${m.from}`}>{m.text}</div>
            ))}
            {loading && (
              <div className="message bot typing-indicator"><span>●</span><span>●</span><span>●</span></div>
            )}
            <div ref={bottomRef} />
          </main>

          {/* Quick-suggestion buttons - moved to be above input */}
          <form className="input-container" onSubmit={sendMessage}>
            {/* Suggested questions above input */}
            <div className="option-buttons">
              {getSuggestions().map((item) => (
                <button
                  key={item.label}
                  className="option-button"
                  onClick={() => handleSuggestionClick(item)}
                  disabled={loading}
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </div>
            
            <div className="input-row">
              <input
                className="message-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask a question…"
                disabled={loading || !agent}
                onFocus={() => setKeyboardOpen(true)}
                onBlur={() => setTimeout(() => setKeyboardOpen(false), 100)}
              />
              <button className="send-button" type="submit" disabled={loading || !input.trim()}>Send</button>
            </div>
          </form>

          {/* Disclaimer moved below input */}
          <p className="chatbot-disclaimer">
            Disclaimer: IRIS provides general information only and does not constitute financial advice. We strive for accuracy, but responses may occasionally be incomplete or incorrect.
          </p>
          
        </div>
      )}
    </div>
  );
} 